import { forwardRef, type InputHTMLAttributes, useId } from 'react';

import { cn } from '../../utils';

export interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const sizeStyles = {
  sm: {
    switch: 'h-4 w-7',
    dot: 'h-3 w-3',
    translate: 'translate-x-3',
  },
  md: {
    switch: 'h-5 w-9',
    dot: 'h-4 w-4',
    translate: 'translate-x-4',
  },
  lg: {
    switch: 'h-6 w-11',
    dot: 'h-5 w-5',
    translate: 'translate-x-5',
  },
  xl: {
    switch: 'h-[30px] w-[56px]',
    dot: 'h-[25px] w-[25px]',
    translate: 'translate-x-[26px]',
  },
};

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  ({ label, description, size = 'md', className, id, disabled, checked, ...props }, ref) => {
    const reactId = useId();
    const inputId = id || `switch-${reactId}`;
    const styles = sizeStyles[size];

    return (
      <label
        htmlFor={inputId}
        className={cn(
          'inline-flex items-center',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          className
        )}
      >
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            type="checkbox"
            className="sr-only peer"
            disabled={disabled}
            checked={checked}
            {...props}
          />
          <div
            className={cn(
              'rounded-full transition-colors duration-200',
              'peer-focus:ring-2 peer-focus:ring-primary-500 peer-focus:ring-offset-2',
              checked ? 'bg-primary-500' : 'bg-tkf-line-medium',
              styles.switch
            )}
          />
          <div
            className={cn(
              'absolute left-0.5 top-0.5 rounded-full bg-tkf-surface shadow transition-transform duration-200',
              checked && styles.translate,
              styles.dot
            )}
          />
        </div>
        {(label || description) && (
          <div className="ml-3">
            {label && <span className="text-sm font-medium text-tkf-fg">{label}</span>}
            {description && <p className="text-sm text-tkf-fg-muted">{description}</p>}
          </div>
        )}
      </label>
    );
  }
);

Switch.displayName = 'Switch';
